import React, { useState, useEffect, useMemo } from 'react';
import { FilledButton } from '@ds/react-components';
import { useParams, useHistory } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import * as moment from 'moment';
import { postSuspension } from 'services/suspensions';
import { defaultStartDate } from 'constants/suspensionConstants';
import {
  setSuspensionAlertMessage,
  getSuspensionFormData,
} from 'utils/suspensions';
import frequencyTypes from 'constants/frequencyTypes';
import SuspensionChargeOptionForAdd from '../common/SuspensionChargeOption/SuspensionChargeOptionForAdd';
import SuspensionReasonTextarea from '../common/SuspensionReasonTextarea';
import SuspensionDateRangePicker from '../common/SuspensionDateRangePicker';
import SuspensionFormCancelButton from '../common/SuspensionFormCancelButton';

const AddSuspensionForm = (props) => {
  const {
    canSuspendIgnoreSuspensionRules,
    suspensionInfo,
    localisation,
    hasSuspensionRuleForAccount,
    updateAppLoading,
    setErrorMsg,
    canChargeForSuspension,
    handleSelectStartDate,
    accountDetails,
  } = props;

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { adfitNo } = useParams();
  const history = useHistory();
  const {
    remainingSuspensionPeriod,
    suspensionFee,
    allowedSuspensionPeriod,
  } = suspensionInfo;
  const {
    handleSubmit,
    register,
    control,
    setValue,
    errors,
    getValues,
    reset,
  } = useForm();
  const { startDate } = accountDetails;
  const accountNotStarted = startDate
    ? moment().isBefore(moment(startDate))
    : false;
  //set up form default values
  const formDefaultValues = useMemo(() => {
    const chargeForSuspensionDefault =
      hasSuspensionRuleForAccount && suspensionFee !== null;
    return canSuspendIgnoreSuspensionRules
      ? {
          suspensionStartDate: accountNotStarted
            ? moment(startDate)
            : defaultStartDate,
          suspensionEndDate: null,
          suspensionReason: null,
          chargeForSuspension: false,
          suspensionFee: 0,
          suspensionPaymentFrequency: frequencyTypes.OneOff.value,
        }
      : {
          suspensionStartDate: accountNotStarted
            ? moment(startDate)
            : defaultStartDate,
          suspensionEndDate: null,
          suspensionReason: null,
          chargeForSuspension: chargeForSuspensionDefault,
          suspensionFee: chargeForSuspensionDefault ? suspensionFee : 0,
          suspensionPaymentFrequency: chargeForSuspensionDefault
            ? frequencyTypes.Weekly.value
            : frequencyTypes.OneOff.value,
        };
  }, [
    accountNotStarted,
    hasSuspensionRuleForAccount,
    suspensionFee,
    canSuspendIgnoreSuspensionRules,
    startDate,
  ]);
  useEffect(() => {
    reset(formDefaultValues);
  }, [formDefaultValues, reset]);

  const watchSuspensionStartDate = useWatch({
    control,
    name: 'suspensionStartDate',
  });

  //Submit the form
  const onSubmitHandler = (data) => {
    const formData = getSuspensionFormData(data, formDefaultValues);

    setIsSubmittingForm(true);
    updateAppLoading(true);
    postSuspension(adfitNo, formData)
      .then((res) => {
        updateAppLoading(false);
        const suspensionAlertMessage = {
          variant: 'success',
          title: 'Suspension added successfully.',
          response: res,
        };
        setSuspensionAlertMessage(suspensionAlertMessage);
        history.push(`/Customers/${adfitNo}/Payments/Suspensions`);
      })
      .catch((err) => {
        setIsSubmittingForm(false);
        updateAppLoading(false);
        console.error(err);
        const result = err.response.json();
        result.then((errorObj) => {
          setErrorMsg(errorObj);
        });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <SuspensionDateRangePicker
        disableStartDate={false}
        minEndDate={moment(watchSuspensionStartDate)}
        /*If remainingSuspensionPeriod is less than or equal to 0, no limitation on max end date, get the validation from API  */
        maxEndDate={
          remainingSuspensionPeriod > 0
            ? moment(watchSuspensionStartDate).add(
                remainingSuspensionPeriod - 1,
                'days'
              )
            : undefined
        }
        {...{
          canChargeForSuspension,
          allowedSuspensionPeriod,
          handleSelectStartDate,
          localisation,
          control,
          setValue,
          getValues,
          errors,
        }}
      />
      {canChargeForSuspension || suspensionFee === null ? (
        <SuspensionChargeOptionForAdd
          register={register}
          control={control}
          errors={errors}
        />
      ) : null}
      <SuspensionReasonTextarea register={register} />
      <FilledButton variant="primary" isDisabled={isSubmittingForm}>
        Add
      </FilledButton>
      <SuspensionFormCancelButton isDisabled={isSubmittingForm} />
    </form>
  );
};

export default AddSuspensionForm;
